import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse, Workflow, CreateWorkflowRequest, NodeTypeDef, DataFile, Intent, Plan, LicenseStatusResponse, MachineFingerprint, WorkflowVersion, WorkflowChangeLog } from '../types';
import { getCurrentLocale } from '../i18n';
import { templateApi } from './templateApi';

// Trace ID 管理
const TRACE_HEADER = 'X-Trace-ID';
const TRACE_CONTEXT_HEADER = 'X-Trace-Context';
const RESPONSE_TRACE_HEADER = 'X-Response-Trace-ID';

/**
 * 生成唯一的 Trace ID
 * 格式: uuid (36字符)
 */
function generateTraceId(): string {
  return crypto.randomUUID();
}

/**
 * 获取或生成 Trace ID
 * 从 sessionStorage 获取当前会话的 Trace ID，如果没有则生成新的
 */
function getOrCreateTraceId(): string {
  let traceId = sessionStorage.getItem('x-trace-id');
  if (!traceId) {
    traceId = generateTraceId();
    sessionStorage.setItem('x-trace-id', traceId);
  }
  return traceId;
}

/**
 * 获取当前 Span ID (用于链式跟踪)
 */
function getCurrentSpanId(): string {
  return sessionStorage.getItem('x-span-id') || '';
}

/**
 * 设置当前 Span ID
 */
function setCurrentSpanId(spanId: string): void {
  sessionStorage.setItem('x-span-id', spanId);
}

/**
 * 生成新的 Span ID
 */
function generateSpanId(): string {
  return crypto.randomUUID().substring(0, 16);
}

/**
 * 构建 Trace Context
 * 格式: traceId:spanId:parentId
 */
function buildTraceContext(): string {
  const traceId = getOrCreateTraceId();
  const spanId = generateSpanId();
  const parentId = getCurrentSpanId();
  
  // 保存当前 span ID 作为后续请求的 parent
  setCurrentSpanId(spanId);
  
  return `${traceId}:${spanId}:${parentId}`;
}

// 创建 axios 实例
export const apiClient: AxiosInstance = axios.create({
  baseURL: (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token、多语言头和 Trace ID
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Language'] = getCurrentLocale();
    
    // 添加 Trace ID 和 Trace Context
    const traceId = getOrCreateTraceId();
    const traceContext = buildTraceContext();
    config.headers[TRACE_HEADER] = traceId;
    config.headers[TRACE_CONTEXT_HEADER] = traceContext;
    
    // 在开发环境下打印请求信息
    if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url} - TraceID: ${traceId}`);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 提取并保存服务端返回的 Trace ID
apiClient.interceptors.response.use(
  (response) => {
    // 从响应头中提取服务端返回的 Trace ID
    const serverTraceId = response.headers[RESPONSE_TRACE_HEADER.toLowerCase()];
    if (serverTraceId) {
      // 可选：保存服务端 Trace ID 用于关联
      sessionStorage.setItem('x-server-trace-id', serverTraceId);
    }
    return response;
  },
  (error) => {
    // 错误响应中也尝试提取 Trace ID
    if (error.response) {
      const serverTraceId = error.response.headers[RESPONSE_TRACE_HEADER.toLowerCase()];
      if (serverTraceId) {
        sessionStorage.setItem('x-server-trace-id', serverTraceId);
        console.error(`[API Error] TraceID: ${serverTraceId}, Status: ${error.response.status}`);
      }
    }
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一错误处理
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // 检查是否是邀请相关的API调用，如果是则不自动跳转，让调用方处理
      const requestUrl = error.config?.url || '';
      const isInviteApi = requestUrl.includes('/invitations/') || requestUrl.includes('/invite-link');
      // 检查是否是登录相关的API调用，登录失败时不应跳转
      const isAuthApi = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/verify-login-code');
      
      if (!isInviteApi && !isAuthApi) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    // 提取后端返回的错误信息
    if (error.response?.data) {
      const errorData = error.response.data as { code?: number; message?: string };
      if (errorData.message) {
        // 创建一个带有后端错误信息的错误对象
        const customError = new Error(errorData.message);
        (customError as { response?: typeof error.response }).response = error.response;
        return Promise.reject(customError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API 方法封装
const api = {
  // GET 请求
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<unknown, ApiResponse<T>>(url, config),

  // POST 请求
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<unknown, ApiResponse<T>>(url, data, config),

  // PUT 请求
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<unknown, ApiResponse<T>>(url, data, config),

  // PATCH 请求
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<unknown, ApiResponse<T>>(url, data, config),

  // DELETE 请求
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<unknown, ApiResponse<T>>(url, config),
};

// Workflow API
export interface ExcelCellBorder {
  color?: string;
  style?: number;
}

export interface ExcelCellStyle {
  fontColor?: string;
  fontFamily?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: string;
  strike?: boolean;
  fillColor?: string;
  fillType?: string;
  alignmentHorizontal?: string;
  alignmentVertical?: string;
  numFmt?: string;
  borders?: Record<string, ExcelCellBorder>;
}

export interface ExcelStylesPayload {
  headerStyles?: Record<string, ExcelCellStyle>;
  // 行索引（0-based）序号作为字符串 key
  cellStyles?: Record<string, Record<string, ExcelCellStyle>>;
  colWidths?: Record<string, number>;
  // 行索引（0-based）序号作为字符串 key
  rowHeights?: Record<string, number>;
  sheetName?: string;
}

export const workflowApi = {
  // 获取节点类型列表
  getNodeTypes: () => api.get<NodeTypeDef[]>('/node-types'),

  // 获取表格流列表（从数据库获取持久化的工作流）
  listWorkflows: () => api.get<{ 
    workflows: Array<{
      id: string;
      name: string;
      description?: string;
      status?: string;
      nodes: Array<{
        id: string;
        type: string;
        config?: Record<string, unknown>;
        position?: { x: number; y: number };
      }>;
      edges: Array<{ from: string; to: string }>;
      nodeCount?: number;
      executionCount?: number;
      requiredFields?: string[];
      createdAt?: string;
      updatedAt?: string;
    }>; 
    total: number 
  }>('/workflows'),

  // 获取表格流详情
  getWorkflow: (id: string) => api.get<Workflow>(`/workflows/${id}`),

  // 创建表格流
  createWorkflow: (data: CreateWorkflowRequest) => api.post<Workflow>('/workflows', data),

  // 更新表格流
  updateWorkflow: (id: string, data: Partial<CreateWorkflowRequest>) =>
    api.put<Workflow>(`/workflows/${id}`, data),

  // 删除表格流
  deleteWorkflow: (id: string) => api.delete<void>(`/workflows/${id}`),

  // ── Store 相关（URL 带 /store，仅内存临时编辑；离开页时需移除）
  createWorkflowInStore: (data: CreateWorkflowRequest & { id?: string }) =>
    api.post<Workflow>('/workflows/store', data),
  getWorkflowFromStore: (id: string) => api.get<Workflow>(`/workflows/store/${id}`),
  updateWorkflowInStore: (id: string, data: Partial<CreateWorkflowRequest>) =>
    api.put<Workflow>(`/workflows/store/${id}`, data),
  deleteWorkflowFromStore: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/workflows/store/${id}`),

  // 验证表格流
  validateWorkflow: (data: CreateWorkflowRequest) =>
    api.post<{ valid: boolean }>('/workflows/validate', data),

  // 执行表格流（传 variables，结果由后端异步处理）
  executeWorkflow: (id: string, variables?: Record<string, unknown>) =>
    api.post<{ workflowId: string; status: string }>(`/workflows/${id}/execute`, { variables }),

  // 预览节点结果
  previewNode: (workflowId: string, nodeId: string) =>
    api.get<unknown>(`/workflows/${workflowId}/preview/${nodeId}`),

  // 工作流版本
  listWorkflowVersions: (workflowId: string) =>
    api.get<{ versions: WorkflowVersion[]; total: number }>(`/workflows/${workflowId}/versions`),

  getWorkflowVersion: (workflowId: string, versionId: string) =>
    api.get<WorkflowVersion>(`/workflows/${workflowId}/versions/${versionId}`),

  rollbackWorkflow: (workflowId: string, versionId: string) =>
    api.post<Workflow>(`/workflows/${workflowId}/rollback/${versionId}`),

  // 校验工作流字段
  validateWorkflowFields: (workflowId: string, columns: string[]) =>
    api.post<{
      valid: boolean;
      requiredFields: string[];
      missingFields: string[];
      message?: string;
    }>(`/workflows/${workflowId}/validate-fields`, { columns }),

  // 获取修改记录
  getWorkflowChangeLogs: (workflowId: string) =>
    api.get<{ logs: WorkflowChangeLog[]; total: number }>(`/workflows/${workflowId}/changelogs`),

  // 锁定工作流进行编辑
  lockWorkflowForEdit: (workflowId: string, sessionId?: string) =>
    api.post<{
      locked: boolean;
      lockInfo?: {
        userId: string;
        userName: string;
        lockedAt: string;
        expiresAt: string;
      };
      message?: string;
    }>(`/workflows/${workflowId}/lock`, { sessionId }),

  // 解锁工作流
  unlockWorkflowForEdit: (workflowId: string) =>
    api.post<{ unlocked: boolean; message?: string }>(`/workflows/${workflowId}/lock`, {}),

  // 获取工作流锁定状态
  getWorkflowLockStatus: (workflowId: string) =>
    api.get<{
      locked: boolean;
      lockInfo?: {
        userId: string;
        userName: string;
        lockedAt: string;
        expiresAt: string;
      };
      canEdit: boolean;
      isLockedBy: boolean;
    }>(`/workflows/${workflowId}/lock`),

  // 延长工作流锁定时间
  extendWorkflowLock: (workflowId: string) =>
    api.post<{
      extended: boolean;
      lockInfo?: {
        userId: string;
        userName: string;
        lockedAt: string;
        expiresAt: string;
      };
    }>(`/workflows/${workflowId}/lock/extend`),

  // 导出工作流为 ZIP 文件
  exportWorkflow: async (
    workflowId: string
  ): Promise<{ blob: Blob; filename?: string }> => {
    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    const response = await fetch(`${baseURL}/workflows/${workflowId}/export`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const text = await response.text();
      // 尝试解析 JSON 错误响应，提取 message 字段
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    // Prefer RFC 5987 filename*=
    const filenameStarMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (filenameStarMatch?.[1]) {
      try {
        return { blob, filename: decodeURIComponent(filenameStarMatch[1]) };
      } catch {
        return { blob, filename: filenameStarMatch[1] };
      }
    }
    // Fallback to filename=""
    const filenameMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (filenameMatch?.[1]) {
      return { blob, filename: filenameMatch[1] };
    }
    return { blob };
  },

  // 从 ZIP 文件导入工作流
  importWorkflow: async (file: File): Promise<Workflow> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    const response = await fetch(`${baseURL}/workflows/import`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      // 尝试解析 JSON 错误响应，提取 message 字段
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    const json = await response.json() as {
      code: number;
      message: string;
      data: Workflow;
    };

    if (json.code !== 0) {
      throw new Error(json.message || '导入失败');
    }

    return json.data;
  },

  // 应用表格流：上传文件并执行，返回结果预览数据
  // useStore：true 时请求 /workflows/store/:id/run（首页或编辑页 store 模式）
  runWorkflow: async (
    workflowId: string,
    inputFiles: Array<{ nodeId: string; file: File }>,
    workflow?: {
      name: string;
      description?: string;
      nodes: Array<{
        id: string;
        type: string;
        position: { x: number; y: number };
        data: { label: string; config: unknown };
      }>;
      edges: Array<{ id: string; source: string; target: string }>;
    },
    useStore?: boolean
  ): Promise<{
    columns: string[];
    rows: Record<string, unknown>[];
    rowCount: number;
    metadata?: Record<string, unknown>;
    excelStyles?: ExcelStylesPayload;
    /** Present when the terminal node is a group_by (or downstream of one without an append). */
    is_grouped?: boolean;
    /** One entry per group, ordered by first occurrence of the group key. */
    grouped_results?: Array<{
      group_key: string;
      columns: string[];
      rows: Record<string, unknown>[];
      rowCount: number;
    }>;
    /** Per-node output column names, populated after execution */
    node_schemas?: Record<string, string[]>;
    /** 执行记录ID，用于分享功能 */
    executionId?: string;
  }> => {
    const formData = new FormData();

    // Append each input-node file with its node ID as the field name.
    // The first file is also appended as 'file' for single-file backend compatibility.
    inputFiles.forEach(({ nodeId, file }, idx) => {
      formData.append(`file_${nodeId}`, file);
      if (idx === 0) formData.append('file', file);
    });

    // 如果提供了表格流定义，则作为 JSON 字符串传递
    if (workflow) {
      // 转换节点格式为后端期望的格式
      const convertedNodes = workflow.nodes.map(node => ({
        id: node.id,
        type: node.type,
        config: node.data.config as Record<string, unknown> || {},
        position: node.position,
      }));

      // 转换边格式为后端期望的格式 (source -> from, target -> to)
      const convertedEdges = workflow.edges.map(edge => ({
        from: edge.source,
        to: edge.target,
      }));

      formData.append('workflow', JSON.stringify({
        id: workflowId,
        name: workflow.name,
        description: workflow.description || '',
        nodes: convertedNodes,
        edges: convertedEdges,
      }));
    }

    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    // Store 编辑时用 /workflows/store/:id/run；带 definition 时用 /workflows/run；否则 /workflows/:id/run
    const url = workflow
      ? `${baseURL}/workflows/run`
      : useStore
        ? `${baseURL}/workflows/store/${workflowId}/run`
        : `${baseURL}/workflows/${workflowId}/run`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      // 尝试解析 JSON 错误响应，提取 message 字段
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    const json = await response.json() as {
      code: number;
      message: string;
      data: {
        columns: string[];
        rows: Record<string, unknown>[];
        rowCount: number;
        metadata?: Record<string, unknown>;
        excelStyles?: ExcelStylesPayload;
        is_grouped?: boolean;
        grouped_results?: Array<{
          group_key: string;
          columns: string[];
          rows: Record<string, unknown>[];
          rowCount: number;
        }>;
        node_schemas?: Record<string, string[]>;
        executionId?: string;
      };
    };

    // Backend uses code=0 for success (see pkg/response/response.go)
    if (json.code !== 0) {
      throw new Error(json.message || '表格流执行失败');
    }

    return json.data;
  },

  // 根据运行结果（含 excelStyles）导出带样式的 Excel
  exportExcel: async (payload: {
    columns: string[];
    rows: Record<string, unknown>[];
    excelStyles?: ExcelStylesPayload;
    fileName?: string;
    sheetName?: string;
  }): Promise<Blob> => {
    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    const response = await fetch(`${baseURL}/workflows/export-excel`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();
      // 尝试解析 JSON 错误响应，提取 message 字段
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    return await response.blob();
  },
};

// AI API
export const aiApi = {
  // 解析意图
  parseIntent: (input: string, context?: { availableTables: { name: string; columns: { name: string; type: string }[] }[] }) =>
    api.post<{ intent: Intent; plan?: Plan; workflow?: string }>('/ai/parse', { input, context }),

  // 生成执行计划
  generatePlan: (intent: Intent, tables?: { name: string; columns: { name: string; type: string }[] }[]) =>
    api.post<{ plan: Plan; workflow: string; validation: { valid: boolean; errors?: { field: string; message: string }[] } }>('/ai/plan', { intent, tables }),

  // 验证计划
  validatePlan: (plan: Plan) =>
    api.post<{ valid: boolean; errors?: { field: string; message: string; stepId?: string }[] }>('/ai/plan/validate', { plan }),

  // 确认计划
  confirmPlan: (planId: string, confirm: boolean, feedback?: string) =>
    api.post<{ confirmed: boolean; workflowId?: string; message: string }>('/ai/plan/confirm', { planId, confirm, feedback }),

  // 获取建议
  getSuggestions: (query?: string) =>
    api.get<{ suggestions: { text: string; description: string }[] }>('/ai/suggestions', { params: { q: query } }),

  // 获取示例
  getExamples: () =>
    api.get<{ examples: { input: string; intent: string; plan: string; category: string }[] }>('/ai/examples'),

  // AI 公式助手 - 根据自然语言描述生成计算公式
  generateFormula: (prompt: string, columns: string[]) =>
    api.post<{ formula: string; description: string }>('/ai/formula', { prompt, columns }),

  /**
   * 流式构建工作流（三阶段 Prompt 串联），通过 onProgress 推送每步进度，onDone 返回完整 intent/plan/workflow
   */
  buildWorkflowStream: async (
    input: string,
    context: BuildStreamContext | undefined,
    callbacks: {
      onProgress: (p: BuildProgress) => void;
      onDone: (data: { intent: Intent; plan: Plan; workflow: string }) => void;
      onError: (message: string) => void;
    }
  ): Promise<void> => {
    const baseURL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';
    const token = localStorage.getItem('token');
    const res = await fetch(`${baseURL}/ai/build-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Language': getCurrentLocale(),
      },
      body: JSON.stringify({ input, context }),
    });
    if (!res.ok) {
      callbacks.onError(res.statusText || 'Request failed');
      return;
    }
    const reader = res.body?.getReader();
    if (!reader) {
      callbacks.onError('No response body');
      return;
    }
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const chunk of lines) {
          if (!chunk.trim()) continue;
          let event = '';
          let dataStr = '';
          for (const line of chunk.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataStr = line.slice(5).trim();
          }
          try {
            const data = dataStr ? JSON.parse(dataStr) : null;
            if (event === 'progress') {
              callbacks.onProgress(data as BuildProgress);
            } else if (event === 'done') {
              callbacks.onDone({
                intent: data.intent,
                plan: data.plan,
                workflow: data.workflow || '',
              });
              return;
            } else if (event === 'error') {
              callbacks.onError(data?.message || 'Unknown error');
              return;
            }
          } catch (_) {
            // ignore parse errors for non-JSON lines
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};

/** 流式构建请求的 context（表信息 + 可选样例数据） */
export interface BuildStreamContext {
  availableTables: { name: string; columns: { name: string; type?: string; semanticType?: string }[] }[];
  /** 表头 + 前 10～15 行，供 AI 意图识别与参数填充 */
  sampleData?: { headers: string[]; rows: Record<string, unknown>[] };
}

/** 构建过程单条进度（与后端 SSE progress 一致） */
export interface BuildProgress {
  stage: string;
  message: string;
  /** 前端 i18n 键：nodes.aiAssistant.buildSteps[messageKey]，插值用 detail/step/total */
  messageKey?: string;
  detail?: unknown;
  step?: number;
  total?: number;
}

// Auth API
export const authApi = {
  // 登录
  login: (username: string, password: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        nickname?: string;
        role?: string;
        emailVerified?: boolean;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
      requires_password_change: boolean;
    }>('/auth/login', { username, password }),

  // 切换语言
  switchLanguage: (language: string) =>
    api.post<{
      message: string;
      language: string;
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/switch-language', { language }),

  // 刷新token
  refreshToken: (refreshToken: string) =>
    api.post<{
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/refresh', { refresh_token: refreshToken }),

  // 登出
  logout: () => api.post<void>('/auth/logout'),

  // 获取当前用户
  getCurrentUser: () =>
    api.get<{
      id: string;
      username: string;
      email?: string;
      name?: string;
      role?: string;
      emailVerified?: boolean;
    }>('/auth/me'),

  // 修改密码
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post<void>('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),

  // ========== 用户注册 ==========
  // 注册
  register: (data: {
    username: string;
    email: string;
    password: string;
    nickname?: string;
  }) => api.post<{
    user: {
      id: string;
      username: string;
      email: string;
      nickname?: string;
      role: string;
    };
    email_sent: boolean;
    verification_url?: string;
  }>('/auth/register', data),

  // 验证邮箱
  verifyEmail: (token: string) =>
    api.post<{
      message: string;
      user: {
        id: string;
        username: string;
        email: string;
        name?: string;
        emailVerified: boolean;
      };
    }>('/auth/verify-email', { token }),

  // 重新发送验证邮件
  resendVerificationEmail: (email: string) =>
    api.post<{
      message: string;
    }>('/auth/resend-verification', { email }),

  // ========== 忘记密码 ==========
  // 忘记密码
  forgotPassword: (email: string) =>
    api.post<{
      message: string;
    }>('/auth/forgot-password', { email }),

  // 验证重置令牌
  validateResetToken: (token: string) =>
    api.get<{
      valid: boolean;
      email?: string;
    }>('/auth/validate-reset-token', { params: { token } }),

  // 重置密码
  resetPassword: (token: string, newPassword: string) =>
    api.post<{
      message: string;
    }>('/auth/reset-password', { token, new_password: newPassword }),

  // ========== 微信登录 ==========
  // 获取微信登录授权URL
  getWeChatAuthURL: () =>
    api.get<{
      auth_url: string;
      state: string;
    }>('/auth/wechat/url'),

  // 微信登录回调
  weChatCallback: (code: string, state: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        nickname?: string;
        role?: string;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
      requires_password_change: boolean;
    }>('/auth/wechat/callback', null, { params: { code, state } }),

  // ========== 邮箱验证码登录 ==========
  // 发送登录验证码
  sendLoginCode: (email: string) =>
    api.post<{
      message: string;
    }>('/auth/send-login-code', { email }),

  // 验证登录验证码
  verifyLoginCode: (email: string, code: string) =>
    api.post<{
      verified: boolean;
      requires_password: boolean;
      user_id?: string;
      user?: {
        id: string;
        username: string;
        email?: string;
        nickname?: string;
        role?: string;
        emailVerified?: boolean;
        ownedTeamId?: string;
      };
      tokens?: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/verify-login-code', { email, code }),

  // 设置密码并登录
  setPasswordAndLogin: (userId: string, password: string, nickname?: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        nickname?: string;
        role?: string;
        emailVerified?: boolean;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/set-password-and-login', { user_id: userId, password, nickname }),

  // ========== Google OAuth 登录 ==========
  // 获取 Google 登录授权URL
  getGoogleAuthURL: () =>
    api.get<{
      auth_url: string;
      state: string;
    }>('/auth/google/url'),

  // Google 登录回调
  googleCallback: (code: string, state: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        nickname?: string;
        role?: string;
        emailVerified?: boolean;
        avatar?: string;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/google/callback', { code, state }),

  // 兼容旧版 Google Token 登录
  googleLogin: (accessToken: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        nickname?: string;
        role?: string;
        emailVerified?: boolean;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/google/login', { access_token: accessToken }),

  // ========== Google Sheets OAuth 授权 ==========
  // 获取 Google Sheets 授权 URL
  getGoogleSheetsAuthURL: () =>
    api.get<{
      authUrl: string;
      state: string;
    }>('/auth/google/sheets/url'),

  // Google Sheets OAuth 回调
  googleSheetsAuthCallback: (code: string, state: string) =>
    api.post<{
      accessToken: string;
      refreshToken: string;
      expiresAt: number;
      scope: string;
    }>('/auth/google/sheets/callback', { code, state }),

  // 刷新 Google Sheets Access Token
  refreshGoogleSheetsToken: (refreshToken: string) =>
    api.post<{
      accessToken: string;
      expiresAt: number;
    }>('/auth/google/sheets/refresh', { refreshToken }),

  // 更新用户资料
  updateProfile: (data: { nickname?: string; avatar?: string }) =>
    api.put<{
      id: string;
      username: string;
      email?: string;
      nickname?: string;
      avatar?: string;
    }>('/auth/me', data),

  // ========== 激活码验证（CN区域）==========
  // 验证激活码（公开，用于注册时预览）
  validateSpecialCode: (code: string) =>
    api.post<{
      valid: boolean;
      plan?: string;
      durationMonths?: number;
      message?: string;
    }>('/auth/activation-code/validate', { code }),

  // 激活激活码（需要认证）
  activateSpecialCode: (code: string) =>
    api.post<{
      success: boolean;
      plan?: string;
      durationMonths?: number;
      expiresAt?: string;
      message?: string;
    }>('/auth/activation-code/activate', { code }),
};

// Plan Config 类型定义
export interface PlanConfig {
  plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
  name: string;
  description: string;
  monthlyPriceUSD: number;
  yearlyPriceUSD: number;
  monthlyPriceCNY: number;
  yearlyPriceCNY: number;
  maxWorkflowExecutions: number;
  maxStorageBytes: number;
  maxTeamsPerUser: number;
  maxMembersPerTeam: number;
  features: string[];
  visible: boolean;
  popular: boolean;
}

// Subscription API
export const subscriptionApi = {
  // 获取计划配置（公开，无需认证）
  getPlanConfigs: (lang?: string) =>
    api.get<PlanConfig[]>(`/subscription/plan-configs${lang ? `?lang=${lang}` : ''}`),

  // 获取账单历史
  getBillingHistory: () => api.get<Array<{
    id: string;
    userId: string;
    date: string;
    amount: number;
    status: string;
    plan?: string;
    description?: string;
    outTradeNo?: string;
    createdAt: string;
  }>>('/subscription/billing'),

  // 获取当前订阅
  getCurrentSubscription: () => api.get<{
    id: string;
    userId: string;
    plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
    status: 'active' | 'expired' | 'canceled' | 'pending';
    startedAt?: string;
    expiresAt?: string;
    createdAt?: string;
    maxWorkflowExecutions: number;
    maxStorageBytes: number;
    currentMonthExecutions: number;
    currentStorageBytes: number;
  }>('/subscription'),

  // 获取用量统计
  getUsageStats: () => api.get<{
    currentMonthExecutions: number;
    maxWorkflowExecutions: number;
    currentStorageBytes: number;
    maxStorageBytes: number;
    executionUsagePercent: number;
    storageUsagePercent: number;
  }>('/subscription/usage'),

  // 获取所有计划（与 plan-configs 相同，支持 lang 返回对应语言的功能列表）
  getAllPlans: (lang?: string) =>
    api.get<{
      plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
      name: string;
      maxWorkflowExecutions: number;
      maxStorageBytes: number;
      features: string[];
    }[]>(`/subscription/plans${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`),

  // 升级订阅（创建支付订单，返回 checkoutUrl 用于跳转收银台）
  upgradeSubscription: (
    plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise',
    durationMonths?: number,
    provider?: string
  ) =>
    api.post<{
      checkoutUrl?: string;
      provider?: string;
      checkoutId?: string;
    }>('/subscription/upgrade', { plan, durationMonths, provider }),

  // 获取待支付订单列表
  getPendingOrders: () =>
    api.get<Array<{
      id: string;
      plan: string;
      durationMonths: number;
      status: string;
      expiresAt: string;
      checkoutUrl?: string;
      provider?: string;
    }>>('/subscription/pending-orders'),

  // 续订
  renewSubscription: (durationMonths: number) =>
    api.post<{
      id: string;
      expiresAt?: string;
    }>('/subscription/renew', { durationMonths }),

  // 检查权限
  checkPermission: (action: 'execute_workflow' | 'store_data', bytes?: number) =>
    api.post<{ allowed: boolean; message: string }>('/subscription/check', { action, bytes }),

  // 获取使用记录列表
  getUsageRecords: (params?: {
    type?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) => api.get<{
    total: number;
    records: Array<{
      id: string;
      userId: string;
      type: string;
      status: string;
      description: string;
      workflowId?: string;
      fileId?: string;
      fileSize?: number;
      duration?: number;
      createdAt: string;
    }>;
  }>('/subscription/usage-records', { params }),

  // 按月份获取使用记录
  getUsageRecordsByMonth: (year: number, month: number) => api.get<{
    total: number;
    records: Array<{
      id: string;
      userId: string;
      type: string;
      status: string;
      description: string;
      workflowId?: string;
      fileId?: string;
      fileSize?: number;
      duration?: number;
      createdAt: string;
    }>;
  }>(`/subscription/usage-records/${year}/${month}`),

  // 微信支付委托代扣
  isWechatpayEnabled: () =>
    api.get<{ enabled: boolean }>('/subscription/wechatpay/enabled'),
  createWechatpaySign: (plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise') =>
    api.post<{ redirectUrl: string; preEntrustwebId: string; outContractCode: string }>(
      '/subscription/wechatpay/sign',
      { plan }
    ),
  terminateWechatpayContract: (plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise') =>
    api.post<void>('/subscription/wechatpay/terminate', { plan }),
  getWechatpayContractStatus: (plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise') =>
    api.get<{ signed: boolean; enabled: boolean; contractId?: string; signedAt?: string }>(
      `/subscription/wechatpay/contract?plan=${plan}`
    ),

  // LemonSqueezy 支付
  isLemonSqueezyEnabled: () =>
    api.get<{ enabled: boolean }>('/subscription/lemonsqueezy/enabled'),
  createLemonSqueezyCheckout: (plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise', yearly: boolean) =>
    api.post<{ checkoutId: string; checkoutUrl: string; status: string }>(
      '/subscription/lemonsqueezy/checkout',
      { plan, yearly }
    ),
  cancelLemonSqueezySubscription: (reason?: string) =>
    api.post<void>('/subscription/lemonsqueezy/cancel', { reason }),
  getLemonSqueezySubscriptionStatus: () =>
    api.get<{
      active: boolean;
      enabled: boolean;
      subscriptionId?: string;
      plan?: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
      status?: string;
      renewsAt?: string;
      endsAt?: string;
    }>('/subscription/lemonsqueezy/status'),

  // Creem 支付
  isCreemEnabled: () =>
    api.get<{ enabled: boolean }>('/payment/config/creem'),
  createCreemCheckout: (plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise', yearly: boolean) =>
    api.post<{ checkoutId: string; checkoutUrl: string; status: string; provider: string }>(
      '/payment/checkout',
      { plan, yearly, provider: 'creem' }
    ),
  getCreemSubscriptionStatus: () =>
    api.get<{
      active: boolean;
      hasSubscription: boolean;
      provider?: string;
      subscriptionId?: string;
      plan?: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
      status?: string;
      currentPeriodEnd?: string;
      nextBilledAt?: string;
      canceledAt?: string;
    }>('/payment/subscription'),
};

// Data API
export const dataApi = {
  // 获取数据列表
  listData: () => api.get<{ data: DataFile[]; total: number }>('/data'),

  // 获取数据详情
  getData: (id: string) => api.get<DataFile>(`/data/${id}`),

  // 上传数据
  uploadData: (file: File, name?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('name', name);
    }
    return api.post<DataFile>('/data/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 删除数据
  deleteData: (id: string) => api.delete<void>(`/data/${id}`),

  // 预览数据
  previewData: (id: string, limit?: number) =>
    api.get<{ data: Record<string, unknown>[] }>(`/data/${id}/preview`, { params: { limit } }),

  // 获取数据结构
  getDataSchema: (id: string) =>
    api.get<{ schema: { name: string; type: string; nullable: boolean; semanticType?: string }[]; statistics: { rowCount: number; columnCount: number; nullCounts: Record<string, number> } }>(`/data/${id}/schema`),

  // 导出数据
  exportData: (id: string, format: 'excel' | 'csv' = 'excel') =>
    api.post<{ downloadUrl: string }>(`/data/${id}/export`, { format }),
};

// License API (仅本地部署)
export const licenseApi = {
  // 获取授权状态
  getLicenseStatus: () => api.get<LicenseStatusResponse>('/license/status'),

  // 获取机器指纹
  getFingerprint: () => api.get<MachineFingerprint>('/license/fingerprint'),

  // 下载指纹采集文件
  downloadFingerprint: () => {
    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';
    return fetch(`${baseURL}/license/download-custom-file`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // 上传 License 文件
  uploadLicense: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<void>('/license/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 获取授权功能列表
  getFeatures: () => api.get<{ features: string[] }>('/license/features'),

  // 刷新授权验证
  refreshLicense: () => api.post<void>('/license/refresh'),
};

// User Management API (仅本地部署，需要 admin 权限)
export const userApi = {
  // 获取用户列表
  listUsers: (params?: { page?: number; pageSize?: number; keyword?: string; status?: string }) =>
    api.get<{
      users: Array<{
        id: string;
        username: string;
        email?: string;
        avatar?: string;
        role?: string;
        status?: string;
        createdAt?: string;
      }>;
      total: number;
      page: number;
      pageSize: number;
    }>('/users', { params }),

  // 创建用户
  createUser: (data: {
    username: string;
    email: string;
    password: string;
    role: 'admin' | 'user';
  }) => api.post<{
    id: string;
    username: string;
    email: string;
    role: string;
  }>('/users', data),

  // 更新用户
  updateUser: (id: string, data: {
    email?: string;
    role?: 'admin' | 'user';
    status?: 'active' | 'inactive' | 'locked';
  }) => api.put<{
    id: string;
    username: string;
    email: string;
    role: string;
    status: string;
  }>(`/users/${id}`, data),

  // 删除用户
  deleteUser: (id: string) => api.delete<void>(`/users/${id}`),

  // 重置密码
  resetPassword: (id: string, newPassword: string) =>
    api.post<void>(`/users/${id}/reset-password`, { newPassword }),

  // 切换用户状态（停用/启用）
  toggleUserStatus: (id: string) =>
    api.post<{ id: string; status: string }>(`/users/${id}/toggle-status`),
};

// Template API
export { templateApi };

// Runtime Config API
export const runtimeConfigApi = {
  // 获取运行时配置（公开接口，无需认证）
  getConfig: () => api.get<{
    region: string;
    domain: string;
    defaultLocale: string;
    supportedLocales: string[];
    loadingSite: string;
    featureFlags: Record<string, boolean>;
    supportedStorageProviders: string[];
    supportedPaymentProviders: string[];
  }>('/runtime-config'),
};

// Payment API
export const paymentApi = {
  // 验证支付状态
  verifyPayment: (params: Record<string, string>) =>
    api.post<{
      success: boolean;
      orderId?: string;
      customerId?: string;
      subscriptionId?: string;
      plan?: string;
      message?: string;
    }>('/payment/verify', params),
  
  // 获取支付提供商配置
  getProviderConfig: (provider: string) =>
    api.get<{
      provider: string;
      enabled: boolean;
      environment?: string;
      publicKey?: string;
    }>(`/payment/config/${provider}`),
  
  // 创建结账
  createCheckout: (plan: 'pro' | 'enterprise', yearly: boolean, provider?: string) =>
    api.post<{
      checkoutId: string;
      checkoutUrl: string;
      status: string;
      provider: string;
    }>('/payment/checkout', { plan, yearly, provider }),
  
  // 获取订阅状态
  getSubscriptionStatus: () =>
    api.get<{
      active: boolean;
      hasSubscription: boolean;
      provider?: string;
      subscriptionId?: string;
      plan?: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
      status?: string;
      currentPeriodEnd?: string;
      nextBilledAt?: string;
      canceledAt?: string;
    }>('/payment/subscription'),
  
  // 取消订阅
  cancelSubscription: (provider?: string) =>
    api.post<void>('/payment/subscription/cancel', { provider }),
};

export default api;
